import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Layers, Shuffle, FileCheck2, Sparkles, Check, ChevronRight, Package, Tag, BookOpen } from 'lucide-react';
import ColorAmbient from '../components/ColorAmbient';
import PrinterStrip from '../components/PrinterStrip';
import RegistrationMark from '../components/RegistrationMark';

const ACCENT = '#E5007E';

const useCases = [
  { name: 'Labels', desc: 'Verify front/back alignment for wrap-around and shrink-sleeve labels.', Icon: Tag },
  { name: 'Packaging', desc: 'Preview folding cartons with inside and outside colors registered.', Icon: Package },
  { name: 'Books & Magazines', desc: 'Proof imposition layouts before committing to a full press run.', Icon: BookOpen },
];

const features = [
  'Perfect front-to-back registration on a single sheet',
  'Automatic imposition preview with fold marks',
  'Compatible with all Barbieri Spectro measurement devices',
  'Print on rigid, flexible, and translucent substrates',
  'Match ISO 12647 targets for both sides simultaneously',
  'Automatic bleed and safety margin validation',
];

const ImpoProofPage = () => {
  return (
    <main className="pt-28 pb-24 bg-white min-h-screen relative overflow-hidden">
      <ColorAmbient variant="warm" intensity="medium" />

      {/* Registration marks */}
      <RegistrationMark className="absolute top-24 left-6 opacity-40" size={24} />
      <RegistrationMark className="absolute top-24 right-6 opacity-40" size={24} />

      {/* Hero */}
      <section className="container-pn relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#0A2A63]/10 text-[#0A2A63] mb-6 shadow-sm">
              <Sparkles className="h-3.5 w-3.5" style={{ color: ACCENT }} />
              <span className="text-xs font-semibold tracking-widest uppercase">Star Product</span>
            </div>
            <img
              src="https://www.proofnation.com/wp-content/uploads/2023/11/Captura-de-tela-2023-11-21-182445.png"
              alt="ImpoProof"
              className="h-16 mb-6 object-contain object-left"
            />
            <h1 className="text-5xl md:text-7xl font-black text-[#050F2E] tracking-tight leading-[0.95]">
              The premier{' '}
              <span className="pn-gradient-text">
                double-sided
              </span>{' '}
              proofing solution
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Print both sides of your proof in perfect registration. Verify layout, folding and imposition
              before you commit to production.
            </p>

            <div className="mt-8 max-w-md">
              <PrinterStrip className="opacity-90" />
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/contact">
                <Button className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-13 px-7 font-bold btn-transition">
                  Request a demo <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/shop">
                <Button variant="outline" className="rounded-full h-13 px-7 font-bold border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white btn-transition bg-white/70 backdrop-blur">
                  See compatible spectros
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-3 max-w-lg gap-4">
              <div>
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">2 sides</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Single sheet</p>
              </div>
              <div className="border-l border-slate-200 pl-4">
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">±0.1<span className="text-[#FF4E50]">mm</span></p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Registration</p>
              </div>
              <div className="border-l border-slate-200 pl-4">
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">100%</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Confidence</p>
              </div>
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5 relative h-[520px]">
            <div className="absolute inset-0 rounded-[28px] overflow-hidden shadow-2xl bg-gradient-to-br from-[#0A2A63] to-[#061a3f]">
              <img
                src="https://images.pexels.com/photos/1440504/pexels-photo-1440504.jpeg"
                alt="ImpoProof double-sided printing"
                className="absolute inset-0 w-full h-full object-cover opacity-70"
              />
              <div className="absolute inset-0 grain-overlay opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050F2E] via-transparent to-transparent" />

              <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Registration</p>
                <p className="text-2xl font-black text-[#0A2A63]">±0.1<span className="text-[#FF4E50]">mm</span></p>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <PrinterStrip />
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 h-24 w-24 rounded-full border-2 border-dashed border-[#FF4E50] animate-spin-slow opacity-70" />
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="container-pn mt-28 relative">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Use cases</p>
          <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight">Wherever both sides matter.</h2>
          <p className="mt-4 text-slate-600">Ideal for jobs where the back matters as much as the front — and where imposition has to be right.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {useCases.map(({ name, desc, Icon }, idx) => (
            <div key={name} className="group bg-white rounded-2xl p-7 border border-slate-100 hover:border-[#0A2A63] hover:shadow-2xl btn-transition">
              <div className="flex items-center justify-between mb-4">
                <span className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(229,0,126,0.14)' }}>
                  <Icon className="h-5 w-5" style={{ color: ACCENT }} />
                </span>
                <span className="text-xs font-mono text-slate-300">0{idx + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-[#050F2E]">{name}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="container-pn mt-24 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center bg-[#0A2A63] text-white rounded-3xl p-10 md:p-14 shadow-2xl overflow-hidden relative">
          <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#E5007E]/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#FFD700]/15 blur-3xl" />

          <div className="lg:col-span-6 relative">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Feature highlights</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">Front &amp; back. Perfectly aligned. Every time.</h2>
            <p className="mt-4 text-slate-300">Trusted by packaging print shops in over 40 countries. Certified for ISO 12647 both sides.</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <div className="flex items-center gap-2 mb-1"><Layers className="h-4 w-4 text-[#E5007E]" /> <span className="text-[10px] uppercase tracking-widest text-slate-400">Layout</span></div>
                <p className="text-lg font-black">Imposition preview</p>
              </div>
              <div className="bg-[#FF4E50] text-[#050F2E] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-1"><Shuffle className="h-4 w-4" /> <span className="text-[10px] uppercase tracking-widest">Both sides</span></div>
                <p className="text-lg font-black">Single sheet</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <div className="flex items-center gap-2 mb-1"><FileCheck2 className="h-4 w-4 text-[#E5007E]" /> <span className="text-[10px] uppercase tracking-widest text-slate-400">Precision</span></div>
                <p className="text-lg font-black">±0.1 mm reg.</p>
              </div>
              <div className="bg-white text-[#050F2E] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-1"><Check className="h-4 w-4" /> <span className="text-[10px] uppercase tracking-widest">Certified</span></div>
                <p className="text-lg font-black">ISO 12647</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <ul className="space-y-3">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-3 bg-white/5 hover:bg-white/10 transition-colors rounded-xl p-4 border border-white/10">
                  <span className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(229,0,126,0.35)' }}>
                    <Check className="h-4 w-4" style={{ color: '#ffb3d4' }} />
                  </span>
                  <span className="font-semibold text-white pt-1.5">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-pn mt-20 relative">
        <div className="rounded-3xl bg-white border border-slate-100 p-10 md:p-14 shadow-xl overflow-hidden relative">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#E5007E]/20 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Ready to see both sides?</p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight text-[#050F2E]">Book a live ImpoProof demo.</h2>
              <p className="mt-3 text-slate-600">Send us a sample job and we&apos;ll produce a proof you can hold in your hands.</p>
            </div>
            <div className="flex md:justify-end flex-wrap gap-3">
              <Link to="/contact">
                <Button className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 px-7 font-bold btn-transition">
                  Get in touch <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/printfactory">
                <Button variant="outline" className="rounded-full h-12 px-7 font-bold border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white btn-transition">
                  See PrintFactory
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ImpoProofPage;
